import React, { ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import Checkbox from './Checkbox';

import AnimateSpinner from '@components/animate-spinner';
import Button from '@components/atoms/button';
import { NotificationPreference } from '@hooks/notifications';

import { Option } from '@components/notifications';

interface NotificationsFormProps {
  isPending: boolean;
  error: string;
  formScope: React.RefObject<HTMLDivElement | null>;
  headingControls: any;
  textControls: any;
  bgControls: any;
  formControls: any;
  buttonScale: any;
  notificationPreference: NotificationPreference | undefined;
  hasEmailConsent: boolean;
  hasPushConsent: boolean;
  consentError: string | null;
  handleFormSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
  isSubmitting: boolean;
  hasNotificationPreferences?: boolean;
  register: any;
  setValue: (name: string, value: any) => void;
  watch: any;
  isAppView: boolean;
  checkboxOptions: {
    iconType: string;
    title: string;
    description: React.ReactNode;
    isChecked: boolean;
    fieldName: string;
    testId: string;
  }[];
  yesOptionProps: {
    value: NotificationPreference;
    name: string;
    emoji: string;
    title: string;
    description: string;
    extraInfo?: ReactNode;
    highlightColor: 'green' | 'blue';
    testId: string;
  };
  noOptionProps: {
    value: NotificationPreference;
    name: string;
    emoji: string;
    title: string;
    description: string;
    extraInfo?: ReactNode;
    highlightColor: 'green' | 'blue';
    testId: string;
  };
}

/**
 * Form component for notification preferences
 */
const Form = React.memo(
  ({
    isPending,
    error,
    formScope,
    buttonScale,
    notificationPreference,
    hasEmailConsent,
    hasPushConsent,
    consentError,
    handleFormSubmit,
    isSubmitting,
    hasNotificationPreferences,
    register,
    setValue,
    watch,
    isAppView,
    checkboxOptions,
    yesOptionProps,
    noOptionProps
  }: NotificationsFormProps) => {
    return (
      <div ref={formScope} className="w-full">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.4 }}
        >
          <form onSubmit={handleFormSubmit} className="space-y-5">
            <div className="space-y-4">
              <div className="space-y-6 py-1">
                <motion.div
                  className="space-y-6"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.9, duration: 0.3 }}
                >
                  <Option
                    {...yesOptionProps}
                    register={register}
                    watch={watch}
                    extraInfo={
                      <>
                        <motion.div
                          className="mt-2 sm:mt-3 bg-white rounded-md border border-green-200"
                          initial={{
                            opacity: 0,
                            scale: 0.95
                          }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 1.0, duration: 0.3 }}
                        >
                          <div className="p-2 sm:p-2">
                            <div className="text-green-800 text-xs font-semibold">
                              <span className="mr-1">🥇</span>
                              Last season, Fantasy Football Hub members used our
                              AI to win over <strong>50,000</strong>{' '}
                              mini-leagues
                            </div>
                          </div>
                        </motion.div>

                        {/* Use exitBeforeEnter in AnimatePresence for framer-motion v4 */}
                        {/* @ts-ignore - AnimatePresence in framer-motion v4 accepts children but TS definition is incorrect */}
                        <AnimatePresence exitBeforeEnter>
                          {notificationPreference ===
                            NotificationPreference.YES &&
                            (isAppView || hasNotificationPreferences) && (
                              <motion.div
                                key="consent-options"
                                className="mt-3 bg-white rounded-md border border-green-100 relative"
                                initial={{
                                  opacity: 0,
                                  height: 0
                                }}
                                animate={{
                                  opacity: 1,
                                  height: 'auto'
                                }}
                                exit={{
                                  opacity: 0,
                                  height: 0
                                }}
                                transition={{ duration: 0.3 }}
                                data-testid="consent-options"
                                style={{ zIndex: 15 }}
                              >
                                <div className="p-4 sm:p-6 p-3">
                                  <div className="text-green-800 text-xs mb-3 font-medium">
                                    How would you like to receive updates?
                                  </div>

                                  <div
                                    className="space-y-4"
                                    style={{
                                      pointerEvents: 'auto'
                                    }}
                                  >
                                    {checkboxOptions.map((option) => (
                                      <Checkbox
                                        key={option.fieldName}
                                        iconType={
                                          option.iconType as 'email' | 'push'
                                        }
                                        title={option.title}
                                        description={option.description}
                                        isChecked={option.isChecked}
                                        onChange={() => {
                                          setValue(
                                            option.fieldName,
                                            !option.isChecked
                                          );
                                        }}
                                        testId={option.testId}
                                      />
                                    ))}
                                  </div>

                                  {consentError && (
                                    <div className="mt-2 text-xs text-red-600 font-medium">
                                      {consentError}
                                    </div>
                                  )}

                                  {!hasNotificationPreferences && (
                                    <div className="text-[10px] text-green-700 mt-3">
                                      You can change these settings anytime from
                                      the preferences page
                                    </div>
                                  )}
                                </div>
                              </motion.div>
                            )}
                          {notificationPreference ===
                            NotificationPreference.YES &&
                            !isAppView &&
                            !hasNotificationPreferences && (
                              <motion.div
                                key="non-app-message"
                                className="hidden"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 0 }}
                                exit={{ opacity: 0 }}
                              >
                                {/* Render nothing but provide a key for AnimatePresence */}
                              </motion.div>
                            )}
                        </AnimatePresence>
                      </>
                    }
                  />

                  <Option
                    {...noOptionProps}
                    register={register}
                    watch={watch}
                  />
                </motion.div>
              </div>
            </div>

            <motion.div
              className="text-xs text-gray-500 p-3 bg-gray-50 rounded leading-relaxed"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2, duration: 0.3 }}
            >
              {!hasNotificationPreferences && (
                <>You can unsubscribe with one click at any time. </>
              )}
              See our{' '}
              <a
                href="/privacy-policy"
                className="text-blue-600 underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
                <span className="sr-only"> (opens in a new tab)</span>
              </a>{' '}
              for more details.
            </motion.div>

            <motion.div
              className="flex flex-col space-y-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.4, duration: 0.3 }}
            >
              <motion.div
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.98 }}
                style={{ scale: buttonScale }}
              >
                <Button
                  variant="primary"
                  className="relative w-full py-3 text-base font-medium overflow-hidden hover:bg-blue-700 transition-colors"
                  disabled={
                    isSubmitting ||
                    isPending ||
                    !notificationPreference ||
                    (notificationPreference === NotificationPreference.YES &&
                      (isAppView || hasNotificationPreferences) &&
                      !hasEmailConsent &&
                      !hasPushConsent)
                  }
                  aria-busy={isSubmitting || isPending}
                  data-testid="submit-button"
                >
                  {(isSubmitting || isPending) && (
                    <AnimateSpinner
                      customClasses={'absolute left-2'}
                      aria-hidden="true"
                    />
                  )}
                  <motion.span
                    animate={{
                      x: isSubmitting || isPending ? 10 : 0,
                      opacity: isSubmitting || isPending ? 0.8 : 1
                    }}
                  >
                    <motion.span
                      key={notificationPreference || 'default'}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                    >
                      {isSubmitting
                        ? 'Saving...'
                        : isPending && !hasNotificationPreferences
                        ? 'Redirecting...'
                        : notificationPreference === NotificationPreference.YES
                        ? 'Confirm and start winning'
                        : 'Confirm and continue'}
                    </motion.span>
                  </motion.span>
                </Button>
              </motion.div>
            </motion.div>

            {/* Use exitBeforeEnter in AnimatePresence for framer-motion v4 */}
            {/* @ts-ignore - AnimatePresence in framer-motion v4 accepts children but TS definition is incorrect */}
            <AnimatePresence exitBeforeEnter>
              {error && (
                <motion.div
                  key="error-message"
                  className="mt-2 text-sm text-center text-red-600 bg-red-50 border border-red-100 rounded-md"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  role="alert"
                  data-testid="submission-error"
                >
                  <div className="p-4 sm:p-6">
                    <div className="font-medium">Error submitting form</div>
                    <div className="mt-1">{error}</div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </form>
        </motion.div>
      </div>
    );
  }
);

Form.displayName = 'Form';

export default Form;
